import React, { memo } from 'react';
import { Helmet } from 'react-helmet';
import { StaticQuery, graphql, useStaticQuery } from 'gatsby';
import { returnLocalisedPath } from 'globalUtils';
import { withLocale } from '../../../utils/hocs/withLocale';
import { returnHrefLangLinks } from './utils';

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteUrl
        defaultImage: image
        twitterUsername
      }
    }
  }
`;

export const SEO = memo(
  withLocale(
    ({
      locale,
      seoMetadata,
      slug,
      title,
      type,
      canonical,
      robots,
      allLocalSlugs,
    }) => {
      const { site } = useStaticQuery(query);

      const {
        siteMetadata: {
          defaultTitle,
          titleTemplate,
          defaultDescription,
          siteUrl,
          defaultImage,
          twitterUsername,
        },
      } = site;

      const faviconUrl = '/favicon-32x32.png';

      const seo = {
        title: (seoMetadata && seoMetadata.title) || defaultTitle,
        description:
          (seoMetadata &&
            seoMetadata.description &&
            seoMetadata.description.description) ||
          defaultDescription,
        image:
          (seoMetadata &&
            seoMetadata.image &&
            `https:${seoMetadata.image.file.url}`) ||
          `${siteUrl}/${defaultImage}`,
        url: `${siteUrl}`,
      };

      const returnCanonicalLinks = () => {
        if (canonical && canonical.url) {
          return (
            <link
              rel='canonical'
              href={`${siteUrl}/${process.env.GATSBY_ZONE}${returnLocalisedPath(
                locale[0],
                canonical.url || ''
              )}`}
              data-baseprotocol='https'
              data-basehost={siteUrl}
            />
          );
        }
      };

      const schemaOrgJSONLD = () => {
        if (type === 'Home') {
          return {
            '@context': 'http://schema.org',
            '@type': 'WebSite',
            url: `${siteUrl}/${process.env.GATSBY_ZONE}${returnLocalisedPath(
              locale[0],
              slug.url || ''
            )}`,
            name: seo.title,
            inLanguage: locale[0],
            image: seo.image,
            publisher: {
              '@type': 'Organization',
              name: 'Nooz Optics',
            },
            keywords: seo.description,
            sameAs: [
              'https://www.facebook.com/noozoptics/',
              'https://www.nooz-optics.com/eu/fr-fr/',
              'https://www.youtube.com/channel/UCn1a0CQV12gRQ1l-UBOtkSQ',
            ],
          };
        }
      };

      return (
        <Helmet
          htmlAttributes={{
            lang: locale[0],
          }}
          title={seo.title}
          titleTemplate={titleTemplate}>
          {/* href lang */}
          {returnHrefLangLinks(siteUrl, allLocalSlugs)}

          {/* Canonicals */}
          {returnCanonicalLinks()}

          {/* favicon */}
          <link
            rel='icon'
            type='image/png'
            sizes='16x16'
            href={faviconUrl}
          />

          <meta
            name='robots'
            content={robots}
          />
          <meta
            name='description'
            content={seo.description}
          />

          {/* Open graph  */}
          <meta
            name='og:url'
            content={seo.url}
          />
          <meta
            name='og:title'
            content={seo.title}
          />
          <meta
            name='og:description'
            content={seo.description}
          />
          <meta
            name='og:image'
            content={seo.image}
          />
          <meta
            name='og:type'
            content='website'
          />

          {/* Twitter card */}
          <meta
            name='twitter:card'
            content={seo.image}
          />
          <meta
            name='twitter:image'
            content={seo.image}
          />
          <meta
            name='twitter:image:src'
            content={seo.image}
          />
          <meta
            name='twitter:title'
            content={seo.title}
          />
          <meta
            name='twitter:creator'
            content={twitterUsername}
          />
          <meta
            name='twitter:description'
            content={seo.description}
          />

          {/* prevent screen orientation */}
          <meta
            httpEquiv='ScreenOrientation'
            content='autoRotate:disabled'
          />

          {/* Schema.org tags */}
          <script type='application/ld+json'>
            {JSON.stringify(schemaOrgJSONLD())}
          </script>
        </Helmet>
      );
    }
  )
);
